import { Minus, Plus, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FaqDocument } from '../../content/faq/types';

interface FaqDocumentViewProps {
  document: FaqDocument;
  onNavigate?: (view: string) => void;
}

const FaqDocumentView: React.FC<FaqDocumentViewProps> = ({ document, onNavigate }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return document.categories;

    return document.categories
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.question.toLowerCase().includes(term) ||
            item.answer.toLowerCase().includes(term) ||
            category.title.toLowerCase().includes(term),
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [document.categories, searchTerm]);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <article className="policy-doc-card faq-doc-card">
      <p className="policy-doc-card__eyebrow">{document.eyebrow}</p>
      <h1 className="policy-doc-card__title">{document.title}</h1>
      {document.summary ? <div className="policy-doc-card__summary">{document.summary}</div> : null}

      <div className="faq-doc-search">
        <Search className="faq-doc-search__icon" aria-hidden />
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="faq-doc-search__input"
          placeholder={t('views.faq.searchPlaceholder')}
          aria-label={t('views.faq.searchPlaceholder')}
        />
      </div>

      {filteredCategories.length === 0 ? (
        <p className="faq-doc-empty">{t('views.faq.docSearchEmpty')}</p>
      ) : (
        filteredCategories.map((category) => (
          <section
            key={category.id}
            id={category.id}
            className="policy-doc-section faq-doc-category scroll-mt-28"
          >
            <h2 className="policy-doc-section__title">{category.title}</h2>
            <div className="faq-doc-list">
              {category.items.map((item) => {
                const isOpen = openId === item.id;
                return (
                  <div
                    key={item.id}
                    className={`faq-doc-item${isOpen ? ' faq-doc-item--open' : ''}`}
                  >
                    <button
                      type="button"
                      className="faq-doc-item__trigger"
                      aria-expanded={isOpen}
                      onClick={() => toggle(item.id)}
                    >
                      <span className="faq-doc-item__question">{item.question}</span>
                      <span className={`faq-doc-item__toggle${isOpen ? ' faq-doc-item__toggle--open' : ''}`} aria-hidden>
                        {isOpen ? <Minus className="h-4 w-4" strokeWidth={2.5} /> : <Plus className="h-4 w-4" strokeWidth={2.5} />}
                      </span>
                    </button>
                    {isOpen ? (
                      <div className="faq-doc-item__answer">
                        <p>{item.answer}</p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      <div className="policy-doc-cta faq-doc-cta">
        <h4 className="policy-doc-cta__title">{t('views.faq.stillHaveQuestions')}</h4>
        <p className="policy-doc-cta__body">{t('views.faq.docCtaBody')}</p>
        <div className="policy-doc-cta__actions">
          <a
            href="/contact-us"
            className="policy-doc-cta__btn policy-doc-cta__btn--primary"
            onClick={(e) => {
              if (onNavigate) {
                e.preventDefault();
                onNavigate('contact-us');
              }
            }}
          >
            {t('views.faq.contactSupport')}
          </a>
          <button
            type="button"
            className="policy-doc-cta__btn policy-doc-cta__btn--secondary"
            onClick={() => onNavigate?.('signup')}
          >
            {t('views.about.getStartedFree')}
          </button>
        </div>
      </div>
    </article>
  );
};

export default FaqDocumentView;
